/* ============================================
   WORK HERO COMPONENT
   The Creative Orchestra - GEMINI Framework
   Hero section for individual work pages
   ============================================ */

import { useMemo } from 'react';
import { motion } from 'framer-motion';
import { Sparkles } from 'lucide-react';
import { useTheme } from '@context/ThemeContext';
import { GEMINI_STAGES } from '@utils/constants';

/**
 * Work Hero Component
 * Hero section with work name, tagline, and stage badge
 * 
 * @param {Object} props
 * @param {Object} props.work - Work data object
 */
const WorkHero = ({ work }) => {
  const { activeTheme } = useTheme();

  // Get stage info
  const stageInfo = useMemo(() => {
    const stage = Object.values(GEMINI_STAGES).find(s => 
      s.works.includes(work.id)
    );
    return stage || { name: work.stage, letter: work.stage[0] };
  }, [work]);

  // Get accent color
  const accentColor = activeTheme?.colors?.accent || work.colorTheme?.accent || '#818CF8';
  const accentRgb = activeTheme?.cssVariables?.['--theme-accent-rgb'] || '129, 140, 248';

  return (
    <section className="relative py-20 md:py-32 overflow-hidden">
      {/* Background decorations */}
      <div className="absolute inset-0 pointer-events-none">
        {/* Gradient orb */}
        <motion.div
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2
            w-[600px] h-[600px] rounded-full blur-3xl opacity-20"
          style={{
            background: `radial-gradient(circle, rgba(${accentRgb}, 0.3) 0%, transparent 70%)`,
          }}
          animate={{
            scale: [1, 1.1, 1],
            opacity: [0.15, 0.25, 0.15],
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            ease: 'easeInOut',
          }}
        />

        {/* Grid pattern */}
        <div 
          className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage: `
              linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px),
              linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)
            `,
            backgroundSize: '60px 60px',
          }}
        />
      </div>

      <div className="relative z-10 max-w-5xl mx-auto px-4 text-center">
        {/* Stage badge */}
        <motion.div
          className="inline-flex items-center gap-2 px-4 py-2 rounded-full
            mb-6 border"
          style={{
            background: `rgba(${accentRgb}, 0.1)`,
            borderColor: `rgba(${accentRgb}, 0.2)`,
          }}
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <span 
            className="text-xs font-bold px-2 py-0.5 rounded"
            style={{ 
              background: `rgba(${accentRgb}, 0.2)`,
              color: accentColor,
            }}
          >
            {stageInfo.letter}
          </span>
          <span className="text-sm text-gray-400">
            {stageInfo.name || work.stage}
          </span>
        </motion.div>

        {/* Work orb */}
        <motion.div
          className="mx-auto mb-8 w-24 h-24 md:w-32 md:h-32 rounded-full
            flex items-center justify-center border-2"
          style={{
            background: `linear-gradient(135deg, rgba(${accentRgb}, 0.3) 0%, rgba(${accentRgb}, 0.1) 100%)`,
            borderColor: `rgba(${accentRgb}, 0.3)`,
            boxShadow: `0 0 60px rgba(${accentRgb}, 0.3)`,
          }}
          initial={{ scale: 0, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.6, type: 'spring' }}
        >
          <Sparkles 
            className="w-10 h-10 md:w-12 md:h-12" 
            style={{ color: accentColor }}
          />
        </motion.div>

        {/* Work name */}
        <motion.h1
          className="text-5xl md:text-7xl lg:text-8xl font-display font-bold mb-4"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          style={{
            background: `linear-gradient(135deg, #ffffff 0%, ${accentColor} 50%, #ffffff 100%)`,
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
          }}
        >
          {work.name}
        </motion.h1>

        {/* Full name */}
        <motion.p
          className="text-xl md:text-2xl text-gray-300 mb-4"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
        >
          {work.fullName}
        </motion.p>

        {/* Tagline */}
        <motion.p
          className="text-lg text-gray-400 max-w-2xl mx-auto"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
        >
          {work.tagline}
        </motion.p>

        {/* Decorative line */}
        <motion.div
          className="mt-12 mx-auto h-px w-32"
          style={{
            background: `linear-gradient(90deg, transparent, ${accentColor}, transparent)`,
          }}
          initial={{ scaleX: 0, opacity: 0 }}
          animate={{ scaleX: 1, opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.5 }}
        />
      </div>
    </section>
  );
};

/**
 * Compact Work Hero (for smaller displays)
 */
export const CompactWorkHero = ({ work }) => {
  const accentColor = work.colorTheme?.accent || '#818CF8';

  return (
    <div className="py-12 px-4 text-center">
      <span 
        className="inline-block text-xs font-medium px-3 py-1 rounded-full mb-4"
        style={{ 
          background: `${accentColor}20`,
          color: accentColor,
        }}
      >
        {work.stage}
      </span>
      <h1 
        className="text-4xl font-display font-bold mb-2"
        style={{ color: accentColor }}
      >
        {work.name}
      </h1>
      <p className="text-gray-400">{work.fullName}</p>
    </div>
  );
};

export default WorkHero;
